import { useQuery } from '@tanstack/react-query'

import { apiClient } from '../../apiClient'

type OntologyClassificationRequest = {
  program_id?: string
  startCreatedDate?: string
  endCreatedDate?: string
}

type ClassificationOntologyAnswer = ClassificationOntologyBase & { class: string }

type ClassificationOntologyQuestion = {
  question: string
  answers: ClassificationOntologyAnswer[]
}
type ClassificationOntologyAnnotation = ClassificationOntologyBase & {
  annotation: string
}

type ClassificationOntologyBase = {
  accuracy: number
  accuracy_trend: number
  count: number
  f1_score: number
  false_negative_percentage: number
  false_positive_percentage: number
  first_top_common_mistake?: string
  second_top_common_mistake?: string
  first_top_common_mistake_percentage?: number
  second_top_common_mistake_percentage?: number
  iou: number
  precision: number
  previous_accuracy: number
  recall: number
  share: number
}

const ClassificationOntologyBaseType: ClassificationOntologyBase | undefined = undefined
const ClassificationOntologyQuestionType: ClassificationOntologyQuestion | undefined = undefined
const ClassificationOntologyAnnotationType: ClassificationOntologyAnnotation | undefined = undefined

type OntologyClassificationResponse<T extends 'question' | 'annotation'> = {
  total: number
  list: T extends 'question' ? ClassificationOntologyQuestion[] : ClassificationOntologyAnnotation[]
  type: T
}

const PROGRAM_QUALITY_METRICS_CLASSIFICATION = 'program-quality-metrics-classification'

function useGetClassification(params: OntologyClassificationRequest) {
  return useQuery({
    queryKey: [PROGRAM_QUALITY_METRICS_CLASSIFICATION, params],
    queryFn: async () => {
      const { data } = await apiClient.get(
        `/int/metrics/quality/programs/${params.program_id}/classification`,
        {
          params: {
            start_date: params.startCreatedDate,
            end_date: params.endCreatedDate,
          },
        }
      )

      return data
    },
    select: (data) => {
      if (!data)
        return { total: 0, type: 'annotation', list: [] } as OntologyClassificationResponse<
          'question' | 'annotation'
        >

      const keys = Object.keys(data)
      const isQuestion = Array.isArray(data[keys[0]])

      const annotations: ClassificationOntologyAnnotation[] = []
      const questions: ClassificationOntologyQuestion[] = []

      keys.forEach((key) => {
        isQuestion
          ? questions.push({ question: key, answers: data[key] })
          : annotations.push({ annotation: key, ...data[key] })
      })

      const response = {
        total: isQuestion ? questions.length : annotations.length,
        list: isQuestion ? questions : annotations,
        type: isQuestion ? 'question' : 'annotation',
      } as OntologyClassificationResponse<'question' | 'annotation'>

      return response
    },
    enabled:
      Boolean(params?.program_id) && Boolean(params?.startCreatedDate) && Boolean(params?.endCreatedDate),
    refetchOnWindowFocus: false,
  })
}

export {
  ClassificationOntologyAnnotationType,
  ClassificationOntologyBaseType,
  ClassificationOntologyQuestionType,
  PROGRAM_QUALITY_METRICS_CLASSIFICATION,
  useGetClassification,
}
